import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CatalogLayout, useViewportSize } from '@ole-ui/ole-ui-components';
// import ExternalIcon from '@patternfly/react-icons/dist/esm/icons/external-link-alt-icon';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import HeroCarousel from 'components/HeroCarousel';
import VideoTourDisplay from 'components/Banners/VideoTourDisplay';

import './styles.scss';
// import { SKILLS_ASSESSMENT_URL } from 'config/constants';
import MyActivityView from 'views/MyActivity';
import MyTrainingView from 'views/MyTraining';
import SubscriptionsTable from 'components/Subscriptions/SubscriptionsTable';
import LaneIds, { laneAnchorId } from 'views/MyTraining/Lanes/types';
import HomeV2Navigation, { HomeViews } from './Navigation';

const NewHome = () => {
  const { t } = useTranslation();
  const { routerStore, classesStore } = useMobxStores();

  const { enrolledLiveEvents, hasCurrentEnrollment } = classesStore;
  const { route } = routerStore;
  const { tab: initialTab, selectedLane } = (route as any).params;

  // TODO: When we switch to new router we can make usage of nested routes and also hash prop for anchoring elements.
  const [activeItem, setActiveItem] = useState(
    (initialTab as HomeViews) || HomeViews.MyActivity,
  );

  const [expandedLane, setExpandedLane] = useState<LaneIds | null>(null);

  const liveEvents = useMemo(
    () => enrolledLiveEvents?.filter((entry) => entry.type !== 'exam'),
    [enrolledLiveEvents],
  );

  const scrollToLane = useCallback((laneId: LaneIds) => {
    setExpandedLane(null);
    setActiveItem(HomeViews.MyTraining);

    const attemptScroll = (retriesLeft: number) => {
      requestAnimationFrame(() => {
        const element = document.getElementById(laneAnchorId(laneId));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (retriesLeft > 0) {
          setTimeout(() => attemptScroll(retriesLeft - 1), 100);
        }
      });
    };

    attemptScroll(10);
  }, []);

  useEffect(() => {
    if (initialTab) {
      setActiveItem(initialTab as HomeViews);
    }

    const isValidLane = Object.values(LaneIds).includes(selectedLane);
    if (isValidLane && initialTab === HomeViews.MyTraining) {
      scrollToLane(selectedLane);
    }
  }, [initialTab, selectedLane]);

  const isMobile = useViewportSize().isLowerThanMd;

  const pageTitleByView: Partial<Record<HomeViews, string>> = {
    [HomeViews.MyActivity]: t('My activity - Red Hat Learning'),
    [HomeViews.MyTraining]: t('My training - Red Hat Learning'),
  };

  const pageTitle =
    pageTitleByView[activeItem] ?? t('My activity - Red Hat Learning');

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <CatalogLayout className="homev2">
        {hasCurrentEnrollment && (
          <HomeV2Navigation
            activeItem={activeItem}
            liveEvents={liveEvents}
            onLaneClick={scrollToLane}
            setActiveItem={setActiveItem}
          />
        )}
        <CatalogLayout.Content>
          <div className="homev2__content">
            <HeroCarousel key="home_hero" className="homev2__hero" />
            <VideoTourDisplay />
            {isMobile && <SubscriptionsTable />}
            {activeItem === HomeViews.MyActivity && (
              <MyActivityView
                onMyTrainingClick={() => setActiveItem(HomeViews.MyTraining)}
                data-analytics-id="my-activity-btn-home-page-lp"
              />
            )}
            {activeItem === HomeViews.MyTraining && (
              <MyTrainingView
                data-analytics-id="my-activity-btn-home-page-lp"
                liveEvents={liveEvents}
                expandedLane={expandedLane}
                setExpandedLane={setExpandedLane}
              />
            )}
          </div>
        </CatalogLayout.Content>
      </CatalogLayout>
    </>
  );
};

export default withErrorBoundary(
  observer(NewHome),
  ComponentError,
  handleError,
);
